import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams, useLocation } from "react-router-dom"
import DOMPurify from "dompurify"
import {
  FiChevronLeft,
  FiChevronRight,
  FiRefreshCw,
  FiCheckCircle,
  FiAward,
  FiCode,
  FiFileText,
  FiExternalLink,
} from "react-icons/fi"

import { markLectureAsComplete } from "../../../services/operations/courseDetailsAPI"
import { updateCompletedLectures } from "../../../slices/viewCourseSlice"
import CourseAskPopup from "./CourseAskPopup"
import QuizLecture from "./QuizLecture"
import LectureNotes from "./LectureNotes"

const VideoDetails = () => {
  const { courseId, sectionId, subSectionId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const { token } = useSelector((state) => state.auth)
  const { courseSectionData, courseEntireData, completedLectures } =
    useSelector((state) => state.viewCourse)

  const [videoData, setVideoData] = useState(null)
  const [previewSource, setPreviewSource] = useState("")
  const [videoEnded, setVideoEnded] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!courseSectionData.length) return
    if (!courseId || !sectionId || !subSectionId) {
      navigate("/dashboard/enrolled-courses")
      return
    }
    const section = courseSectionData.find((s) => s._id === sectionId)
    const sub = section?.subSection.find((s) => s._id === subSectionId)
    setVideoData(sub ?? null)
    setPreviewSource(courseEntireData?.thumbnail ?? "")
    setVideoEnded(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseSectionData, courseEntireData, location.pathname])

  /* ── Navigation helpers ─────────────────────────────────── */
  const getSectionAndSubIdx = () => {
    const secIdx = courseSectionData.findIndex((d) => d._id === sectionId)
    const subIdx =
      courseSectionData[secIdx]?.subSection.findIndex(
        (d) => d._id === subSectionId
      ) ?? -1
    return { secIdx, subIdx }
  }

  const isFirstVideo = () => {
    const { secIdx, subIdx } = getSectionAndSubIdx()
    return secIdx === 0 && subIdx === 0
  }

  const isLastVideo = () => {
    const { secIdx, subIdx } = getSectionAndSubIdx()
    const subLen = courseSectionData[secIdx]?.subSection.length ?? 0
    return secIdx === courseSectionData.length - 1 && subIdx === subLen - 1
  }

  const goToNextVideo = () => {
    const { secIdx, subIdx } = getSectionAndSubIdx()
    const section = courseSectionData[secIdx]
    if (subIdx < section.subSection.length - 1) {
      navigate(
        `/view-course/${courseId}/section/${sectionId}/sub-section/${section.subSection[subIdx + 1]._id}`
      )
    } else if (secIdx < courseSectionData.length - 1) {
      const next = courseSectionData[secIdx + 1]
      navigate(
        `/view-course/${courseId}/section/${next._id}/sub-section/${next.subSection[0]._id}`
      )
    }
  }

  const goToPrevVideo = () => {
    const { secIdx, subIdx } = getSectionAndSubIdx()
    if (subIdx > 0) {
      navigate(
        `/view-course/${courseId}/section/${sectionId}/sub-section/${courseSectionData[secIdx].subSection[subIdx - 1]._id}`
      )
    } else {
      const prev = courseSectionData[secIdx - 1]
      navigate(
        `/view-course/${courseId}/section/${prev._id}/sub-section/${prev.subSection[prev.subSection.length - 1]._id}`
      )
    }
  }

  /* ── Mark complete ──────────────────────────────────────── */
  const handleLectureCompletion = async () => {
    setLoading(true)
    const res = await markLectureAsComplete(
      { courseId, subsectionId: subSectionId },
      token
    )
    if (res) dispatch(updateCompletedLectures(subSectionId))
    setLoading(false)
  }

  const isCompleted     = completedLectures.includes(subSectionId)
  const isQuiz          = videoData?.type === "quiz"
  const totalLectures   = courseSectionData.reduce((n, s) => n + s.subSection.length, 0)
  const completionPct   = totalLectures === 0 ? 0 : Math.round((completedLectures.length / totalLectures) * 100)
  const canTakeAssessment = completionPct >= 80

  /* ── Nav bar (shared by video + content-only lectures) ──── */
  const NavBar = () => (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-richblack-700 bg-richblack-800 px-4 py-3">
      <div className="flex gap-2">
        {!isFirstVideo() && (
          <button
            onClick={goToPrevVideo}
            className="flex items-center gap-1.5 rounded-lg border border-richblack-600 px-4 py-2 text-sm font-medium text-richblack-100 transition-all hover:bg-richblack-700"
          >
            <FiChevronLeft size={15} /> Prev
          </button>
        )}
      </div>

      <div className="flex gap-2">
        {!isCompleted && !isQuiz && (
          <button
            disabled={loading}
            onClick={handleLectureCompletion}
            className="flex items-center gap-2 rounded-lg bg-yellow-50 px-4 py-2 text-sm font-semibold text-richblack-900 transition-all hover:bg-yellow-100 active:scale-95 disabled:opacity-60"
          >
            <FiCheckCircle size={14} />
            {loading ? "Saving…" : "Mark Complete"}
          </button>
        )}

        {!isLastVideo() ? (
          <button
            onClick={goToNextVideo}
            className="flex items-center gap-1.5 rounded-lg bg-yellow-50 px-4 py-2 text-sm font-semibold text-richblack-900 transition-all hover:bg-yellow-100"
          >
            Next <FiChevronRight size={15} />
          </button>
        ) : canTakeAssessment ? (
          <button
            onClick={() => navigate(`/dashboard/assessment/${courseId}`)}
            className="flex items-center gap-2 rounded-lg bg-caribbeangreen-300 px-4 py-2 text-sm font-semibold text-richblack-900 transition-all hover:bg-caribbeangreen-200 active:scale-95"
          >
            <FiAward size={14} /> Take Assessment
          </button>
        ) : null}
      </div>
    </div>
  )

  return (
    <div className="flex flex-col gap-5 pb-10 text-white">

      {/* ── Quiz type ────────────────────────────────────────── */}
      {isQuiz ? (
        <div className="mt-4 flex flex-col gap-4">
          {/* Quiz header */}
          <div className="flex items-center gap-3 rounded-xl border border-yellow-50/20 bg-yellow-50/5 px-4 py-3">
            <FiCode size={18} className="shrink-0 text-yellow-50" />
            <div>
              <p className="text-sm font-semibold text-yellow-50">{videoData?.title}</p>
              <p className="text-xs text-richblack-400">In-course quiz · complete to mark progress</p>
            </div>
            {isCompleted && (
              <span className="ml-auto flex items-center gap-1.5 rounded-full bg-caribbeangreen-300/15 px-3 py-1 text-xs font-semibold text-caribbeangreen-300">
                <FiCheckCircle size={12} /> Done
              </span>
            )}
          </div>

          {/* Instructions */}
          {videoData?.description && (
            <div
              className="prose prose-invert max-w-none text-sm text-richblack-200"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(videoData.description) }}
            />
          )}

          {/* Quiz component */}
          <QuizLecture
            key={subSectionId}
            quizData={videoData?.quizData}
            subSectionId={subSectionId}
            courseId={courseId}
            isCompleted={isCompleted}
          />

          <NavBar />

          <LectureNotes subSectionId={subSectionId} courseId={courseId} />
        </div>
      ) : (
        /* ── Video / content-only type ──────────────────────── */
        <>
          <div className="relative mt-4">
            {videoData?.videoUrl ? (
              <div className="relative overflow-hidden rounded-xl bg-black">
                <video
                  key={videoData.videoUrl}
                  src={videoData.videoUrl}
                  poster={previewSource || undefined}
                  controls
                  controlsList="nodownload"
                  playsInline
                  onEnded={() => setVideoEnded(true)}
                  className="w-full"
                  style={{ display: "block", aspectRatio: "16/9", background: "#000" }}
                />

                {/* Video-ended overlay */}
                {videoEnded && (
                  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-xl bg-gradient-to-t from-black/90 via-black/70 to-transparent">
                    {!isCompleted && (
                      <button
                        disabled={loading}
                        onClick={handleLectureCompletion}
                        className="flex items-center gap-2 rounded-lg bg-yellow-50 px-5 py-2.5 text-sm font-semibold text-richblack-900 shadow transition-all hover:bg-yellow-100 active:scale-95 disabled:opacity-60"
                      >
                        <FiCheckCircle size={15} />
                        {loading ? "Saving…" : "Mark as Complete"}
                      </button>
                    )}

                    <button
                      disabled={loading}
                      onClick={(e) => {
                        const vid = e.currentTarget.closest(".relative").querySelector("video")
                        if (vid) { vid.currentTime = 0; vid.play(); setVideoEnded(false) }
                      }}
                      className="flex items-center gap-2 rounded-lg border border-richblack-500 bg-richblack-800/80 px-5 py-2.5 text-sm font-medium text-richblack-100 transition-all hover:bg-richblack-700 active:scale-95 disabled:opacity-60"
                    >
                      <FiRefreshCw size={14} /> Rewatch
                    </button>

                    <div className="mt-2 flex flex-wrap justify-center gap-3">
                      {!isFirstVideo() && (
                        <button disabled={loading} onClick={goToPrevVideo}
                          className="flex items-center gap-1.5 rounded-lg border border-richblack-500 bg-richblack-800/80 px-4 py-2 text-sm font-medium text-richblack-100 transition-all hover:bg-richblack-700 disabled:opacity-60">
                          <FiChevronLeft size={15} /> Prev
                        </button>
                      )}
                      {!isLastVideo() ? (
                        <button disabled={loading} onClick={goToNextVideo}
                          className="flex items-center gap-1.5 rounded-lg bg-yellow-50 px-4 py-2 text-sm font-semibold text-richblack-900 transition-all hover:bg-yellow-100 disabled:opacity-60">
                          Next <FiChevronRight size={15} />
                        </button>
                      ) : canTakeAssessment ? (
                        <button onClick={() => navigate(`/dashboard/assessment/${courseId}`)}
                          className="flex items-center gap-2 rounded-lg bg-caribbeangreen-300 px-5 py-2 text-sm font-semibold text-richblack-900 transition-all hover:bg-caribbeangreen-200 active:scale-95">
                          <FiAward size={15} /> Take Assessment
                        </button>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            ) : videoData ? (
              /* Content-only lecture (no video) */
              <div className="flex min-h-[200px] items-center justify-center rounded-xl border border-richblack-700 bg-richblack-800 px-6 py-10">
                <div className="flex flex-col items-center gap-3 text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-richblack-600 bg-richblack-700">
                    <FiFileText size={24} className="text-richblack-300" />
                  </div>
                  <p className="text-sm font-medium text-richblack-200">Reading / Notes lecture</p>
                  <p className="text-xs text-richblack-500">Review the content below, then mark as complete.</p>
                  {!isCompleted && (
                    <button
                      disabled={loading}
                      onClick={handleLectureCompletion}
                      className="mt-2 flex items-center gap-2 rounded-lg bg-yellow-50 px-5 py-2 text-sm font-semibold text-richblack-900 transition-all hover:bg-yellow-100 active:scale-95 disabled:opacity-60"
                    >
                      <FiCheckCircle size={14} />
                      {loading ? "Saving…" : "Mark as Complete"}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              /* Nothing selected yet */
              <div className="flex aspect-video w-full items-center justify-center overflow-hidden rounded-xl bg-richblack-800">
                {previewSource ? (
                  <img src={previewSource} alt="Course thumbnail" className="h-full w-full object-cover" />
                ) : (
                  <p className="text-sm text-richblack-400">Select a lecture to begin</p>
                )}
              </div>
            )}
          </div>

          {/* ── Title + completed badge ──────────────────────── */}
          <div className="flex items-start justify-between gap-4">
            <h1 className="text-2xl font-bold leading-snug text-richblack-5">{videoData?.title}</h1>
            {isCompleted && (
              <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-caribbeangreen-300/15 px-3 py-1 text-xs font-semibold text-caribbeangreen-300">
                <FiCheckCircle size={12} /> Completed
              </span>
            )}
          </div>

          {/* ── Description ─────────────────────────────────── */}
          {videoData?.description && (
            <div
              className="prose prose-invert max-w-none pb-4 text-sm text-richblack-200"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(videoData.description) }}
            />
          )}

          {/* ── PDF / Resource links ────────────────────────── */}
          {videoData?.pdfLinks?.length > 0 && (
            <div className="flex flex-col gap-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-richblack-400">
                Resources
              </p>
              <div className="flex flex-col gap-2">
                {videoData.pdfLinks.map((link, i) => (
                  <a
                    key={i}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 rounded-xl border border-richblack-700 bg-richblack-800 px-4 py-3 text-sm text-richblack-100 transition-colors hover:border-richblack-500 hover:text-richblack-5"
                  >
                    <FiFileText size={15} className="shrink-0 text-caribbeangreen-300" />
                    <span className="flex-1 truncate">{link.name || link.url}</span>
                    <FiExternalLink size={13} className="shrink-0 text-richblack-500" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* ── Assessment CTA banner ────────────────────────── */}
          {canTakeAssessment && (
            <div className="flex items-center justify-between gap-4 rounded-2xl border border-caribbeangreen-300/30 bg-caribbeangreen-300/5 px-5 py-4">
              <div className="flex items-center gap-3">
                <FiAward size={20} className="shrink-0 text-caribbeangreen-300" />
                <div>
                  <p className="text-sm font-semibold text-caribbeangreen-300">Ready for the Assessment?</p>
                  <p className="text-xs text-richblack-400">
                    You've completed {completionPct}% of this course. Take the exam to earn your certificate.
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate(`/dashboard/assessment/${courseId}`)}
                className="shrink-0 rounded-xl bg-caribbeangreen-300 px-4 py-2 text-xs font-semibold text-richblack-900 transition-all hover:bg-caribbeangreen-200 active:scale-95"
              >
                Take Now
              </button>
            </div>
          )}

          <LectureNotes subSectionId={subSectionId} courseId={courseId} />
        </>
      )}

      {/* ── AI Doubt Assistant ───────────────────────────────── */}
      {!isQuiz && (
        <CourseAskPopup
          courseName={courseEntireData?.courseName}
          lectureTitle={videoData?.title}
          lectureDescription={videoData?.description}
        />
      )}
    </div>
  )
}

export default VideoDetails
